import { Stack, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { deleteRepoIntegration, getProjectRepos, ProjectRepo, qk } from 'api'
import { Dialog, DialogContent, ToastContent } from 'elements/feedback'
import { Button } from 'elements/inputs'
import { useBoolean } from 'lib/hooks'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EditGitIntegration } from '../edit-git-integration'
import { IntegrationItem } from '../integration-item'
import { IntegrationsBlock } from '../integrations-block'
import { CreateGitLabIntegrationForm } from './create-gitlab-integration-form'

type GitLabIntegrationsProps = {
  projectId: string
}

export const GitLabIntegrations = ({ projectId }: GitLabIntegrationsProps) => {
  const { t } = useTranslation()

  const [searchParams, setSearchParams] = useSearchParams()
  const code = searchParams.get('code')

  const $repos = useQuery(
    qk.projects.reposList.toKeyWithArgs({ projectId }),
    () => getProjectRepos({ projectId }),
  )

  const $deleteGitLabIntegration = useMutation(deleteRepoIntegration)

  const [selectedIntegration, setSelectedIntegration] =
    useState<ProjectRepo | null>(null)

  const createIntegrationDialogOpen = useBoolean(code ? true : false)

  const [integrationToDelete, setIntegrationToDelete] =
    useState<ProjectRepo | null>(null)

  const closeCreateIntegrationDialog = () => {
    createIntegrationDialogOpen.setFalse()
    searchParams.delete('code')
    setSearchParams(searchParams)
  }

  const closeDetailsDialog = () => {
    setSelectedIntegration(null)
  }

  const closeDeleteIntegrationDialog = () => {
    setIntegrationToDelete(null)
  }

  if ($repos.isLoading || $repos.isError) return null

  const gitLabRepos = $repos.data.filter(repo => repo.type === 'gitlab') ?? []

  return (
    <>
      <IntegrationsBlock
        variant="GITLAB"
        connectButton={
          <Button
            variant="text"
            sx={{ color: 'primary.main' }}
            onClick={createIntegrationDialogOpen.setTrue}
          >
            {gitLabRepos.length > 0
              ? t('projects.settings.integrations.connect_new_integration')
              : t('common.connect')}
          </Button>
        }
        connectedIntegrations={
          gitLabRepos.length > 0 ? (
            <Stack spacing={2.5}>
              {gitLabRepos.map(repo => (
                <IntegrationItem
                  key={repo.repoId}
                  title={repo.repo}
                  subtitle={repo.baseBranch}
                  onEdit={() => setSelectedIntegration(repo)}
                  onDelete={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    setIntegrationToDelete(repo)
                  }}
                />
              ))}
            </Stack>
          ) : undefined
        }
      />

      <Dialog open={selectedIntegration !== null} onClose={closeDetailsDialog}>
        {selectedIntegration !== null && (
          <EditGitIntegration
            type="GITLAB"
            projectId={projectId}
            projectRepo={selectedIntegration}
            onClose={closeDetailsDialog}
          />
        )}
      </Dialog>

      <Dialog
        open={createIntegrationDialogOpen.isTrue}
        onClose={closeCreateIntegrationDialog}
      >
        <CreateGitLabIntegrationForm
          projectId={projectId}
          closeDialog={closeCreateIntegrationDialog}
          code={code}
        />
      </Dialog>

      <Dialog
        open={integrationToDelete !== null}
        onClose={closeDeleteIntegrationDialog}
      >
        {integrationToDelete !== null && (
          <DialogContent
            variant="danger"
            title={t('projects.settings.integrations.remove_integration')}
            isActionLoading={$deleteGitLabIntegration.isLoading}
            onConfirm={() => {
              $deleteGitLabIntegration.mutate(
                { projectId, repoId: integrationToDelete.repoId },
                {
                  onSuccess: () => {
                    closeDeleteIntegrationDialog()
                    $repos.refetch()
                    toast.success(
                      <ToastContent
                        title={t(
                          'projects.settings.integrations.github_integration_successfully_deleted',
                          { repoName: integrationToDelete.repo },
                        )}
                      />,
                    )
                  },
                },
              )
            }}
            onDeny={closeDeleteIntegrationDialog}
            confirmText={t('common.remove')}
            denyText={t('common.cancel')}
          >
            <Typography variant="caption1" color="text.secondary">
              <Trans
                i18nKey="projects.settings.integrations.delete_integration_confirmation"
                values={{
                  integrationName: integrationToDelete.repo,
                }}
              />
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}
